import { Flex, Stack, Text } from '@mantine/core';
import { getPersonInitials } from '@/services/person-initials';
import { ReactNode } from 'react';

/**
 * Canonical "identity row" used across the left-panel sections that display
 * a person- or contact-like item — Contact info, Related contacts, Care team
 * members. Visually unifies what were three different layouts into one shape:
 *
 *   [leading 24px]  Primary · Secondary       [trailing]
 *                   Tertiary (if present)
 *
 * - Leading slot (24px square): an Avatar `sm` for people, or a 16px icon
 *   centered in the slot for contact-point data (phone / email).
 * - Primary: 14px / 20px / 400 / full Black — the name OR the contact value.
 * - Secondary: 14px / 20px / 400 / 0.55 black — inline after primary,
 *   separated by a `·` middle dot (canon for inline metadata, same as the
 *   `Name · DoB (Age)` pattern in the patient header).
 * - Tertiary (optional): 12px / 16px / 400 / 0.55 black — `orbit-meta` style.
 *   Used for the related-contact phone number, never for primary identity.
 * - Trailing slot (optional): right-anchored small indicator (primary star,
 *   conversation-lead glyph, etc.). Vertically centered with the first line.
 * - `emphasized`: bumps primary to fw 500 for "flag" emphasis (e.g. primary
 *   contact). Canon allows bold for true emphasis (unread / flag / alert).
 */
interface IdentityRowProps {
  leading: ReactNode;
  primary: string;
  secondary?: string;
  tertiary?: string;
  trailing?: ReactNode;
  emphasized?: boolean;
}

const LEADING_SIZE = 24;
const ROW_GAP = 8;

const PRIMARY_BASE_STYLE = {
  fontSize: 14,
  lineHeight: '20px',
  color: 'var(--mantine-color-black)',
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis' as const,
  whiteSpace: 'nowrap' as const,
  // flex: 1 + minWidth: 0 makes the primary text the row's truncation pivot —
  // it absorbs available space and ellipsis-truncates when content is too
  // long, instead of pushing the secondary text / separator (and downstream
  // trailing actions slot) past the row edge.
  flex: 1,
  minWidth: 0,
};

const SECONDARY_STYLE = {
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 400 as const,
  color: 'rgba(0, 0, 0, 0.55)',
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis' as const,
  whiteSpace: 'nowrap' as const,
  minWidth: 0,
};

const SEPARATOR_STYLE = {
  fontSize: 14,
  lineHeight: '20px',
  color: 'rgba(0, 0, 0, 0.55)',
  flexShrink: 0 as const,
};

const TERTIARY_STYLE = {
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 400 as const,
  color: 'rgba(0, 0, 0, 0.55)',
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis' as const,
  whiteSpace: 'nowrap' as const,
};

export function IdentityRow({ leading, primary, secondary, tertiary, trailing, emphasized = false }: IdentityRowProps) {
  return (
    <Flex align="flex-start" gap={ROW_GAP} w="100%" style={{ maxWidth: '100%', overflow: 'hidden' }}>
      <Flex align="center" justify="center" style={{ width: LEADING_SIZE, height: LEADING_SIZE, flexShrink: 0 }}>
        {leading}
      </Flex>
      <Stack gap={2} style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Flex align="baseline" gap={6} style={{ minWidth: 0 }}>
          <Text style={{ ...PRIMARY_BASE_STYLE, fontWeight: emphasized ? 500 : 400 }}>{primary}</Text>
          {secondary && (
            <>
              <Text style={SEPARATOR_STYLE}>·</Text>
              <Text style={SECONDARY_STYLE}>{secondary}</Text>
            </>
          )}
        </Flex>
        {tertiary && <Text style={TERTIARY_STYLE}>{tertiary}</Text>}
      </Stack>
      {trailing && (
        <Flex align="center" style={{ flexShrink: 0, height: 20 }}>
          {trailing}
        </Flex>
      )}
    </Flex>
  );
}

/**
 * Helper for the leading slot when no photo / resource avatar is available.
 * Extracts up to 2 uppercase initials from a name string. Used by the people
 * sections (Related contacts, Care team) to render a Mantine Avatar sm with
 * the canonical 24px circle + initials.
 */
export function getInitials(name: string | undefined | null): string {
  return getPersonInitials(name);
}
